import pmCommitmentService from '@/services/pmCommitmentService';
import pmConstraintService from '@/services/pmConstraintService';
import pmDecisionService from '@/services/pmDecisionService';
import pmMilestoneService from '@/services/pmMilestoneService';
import {
  ARCHIVE_PERIOD_OPTIONS,
  buildArchivedEntries,
  filterArchivedEntriesByPeriod,
  filterArchivedEntriesBySearch,
  filterArchivedEntriesByType,
  groupArchivedEntriesByMonth,
} from '@/utils/userProjectsArchive';

export default {
  namespaced: true,

  state: {
    entries: [],
    typeFilter: 'all',
    periodFilter: ARCHIVE_PERIOD_OPTIONS[0].value,
    searchQuery: '',
    loading: false,
    error: null,
    projectId: null,
  },

  getters: {
    entries: (state) => state.entries,
    typeFilter: (state) => state.typeFilter,
    periodFilter: (state) => state.periodFilter,
    searchQuery: (state) => state.searchQuery,
    loading: (state) => state.loading,
    error: (state) => state.error,
    projectId: (state) => state.projectId,
    filteredEntries: (state) => {
      const byType = filterArchivedEntriesByType(state.entries, state.typeFilter);
      const byPeriod = filterArchivedEntriesByPeriod(byType, state.periodFilter);
      return filterArchivedEntriesBySearch(byPeriod, state.searchQuery);
    },
    entryCount: (state, getters) => getters.filteredEntries.length,
    isEmpty: (state, getters) => !state.loading && !state.error && getters.filteredEntries.length === 0,
    groupedEntries: (state, getters, rootState) => {
      const locale = rootState?.auth?.user?.language || undefined;
      return groupArchivedEntriesByMonth(getters.filteredEntries, locale);
    },
  },

  mutations: {
    SET_LOADING(state, loading) {
      state.loading = loading;
    },
    SET_ERROR(state, error) {
      state.error = error;
    },
    SET_PROJECT_ID(state, projectId) {
      state.projectId = projectId || null;
    },
    SET_ENTRIES(state, entries) {
      state.entries = entries || [];
    },
    SET_TYPE_FILTER(state, typeFilter) {
      state.typeFilter = typeFilter || 'all';
    },
    SET_PERIOD_FILTER(state, periodFilter) {
      state.periodFilter = periodFilter || ARCHIVE_PERIOD_OPTIONS[0].value;
    },
    SET_SEARCH_QUERY(state, searchQuery) {
      state.searchQuery = searchQuery || '';
    },
    RESET(state) {
      state.entries = [];
      state.typeFilter = 'all';
      state.periodFilter = ARCHIVE_PERIOD_OPTIONS[0].value;
      state.searchQuery = '';
      state.loading = false;
      state.error = null;
      state.projectId = null;
    },
  },

  actions: {
    setTypeFilter({ commit }, typeFilter) {
      commit('SET_TYPE_FILTER', typeFilter);
    },

    setPeriodFilter({ commit }, periodFilter) {
      commit('SET_PERIOD_FILTER', periodFilter);
    },

    setSearchQuery({ commit }, searchQuery) {
      commit('SET_SEARCH_QUERY', searchQuery);
    },

    async fetchArchiveEntries({ commit }, projectId) {
      if (!projectId) {
        commit('SET_PROJECT_ID', null);
        commit('SET_ENTRIES', []);
        return;
      }

      commit('SET_PROJECT_ID', projectId);
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);

      try {
        const [
          commitmentsResult,
          constraintsResult,
          decisionsResult,
          milestonesResult,
        ] = await Promise.allSettled([
          pmCommitmentService.listCommitments(projectId),
          pmConstraintService.listConstraints(projectId),
          pmDecisionService.listDecisions(projectId),
          pmMilestoneService.listMilestones(projectId),
        ]);

        const failures = [
          commitmentsResult,
          constraintsResult,
          decisionsResult,
          milestonesResult,
        ].filter((result) => result.status === 'rejected');

        if (failures.length === 4) {
          const error = failures[0].reason;
          throw error;
        }

        if (failures.length > 0) {
          const message = failures
            .map((result) => result.reason?.response?.data?.message || result.reason?.message)
            .filter(Boolean)
            .join('; ');
          commit('SET_ERROR', message || 'Some archive sources failed to load');
        }

        commit('SET_ENTRIES', buildArchivedEntries({
          commitments: commitmentsResult.status === 'fulfilled'
            ? commitmentsResult.value.items || []
            : [],
          constraints: constraintsResult.status === 'fulfilled'
            ? constraintsResult.value.items || []
            : [],
          decisions: decisionsResult.status === 'fulfilled'
            ? decisionsResult.value.items || []
            : [],
          milestones: milestonesResult.status === 'fulfilled'
            ? milestonesResult.value.items || []
            : [],
        }));
      } catch (error) {
        commit('SET_ERROR', error?.response?.data?.message || error?.message || 'Failed to load archive entries');
        commit('SET_ENTRIES', []);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    async refresh({ dispatch, state }) {
      await dispatch('fetchArchiveEntries', state.projectId);
    },
  },
};
